import type {
  VideoWall,
  VideoWallOperationalState,
  VideoWallPublication,
  VideoWallTileConnection,
} from '@ghostscreen/contracts';
import { i18n } from '../../i18n/i18n';

export const WALL_ARRANGEMENTS = [
  { rows: 1, cols: 2, label: '1×2' },
  { rows: 1, cols: 3, label: '1×3' },
  { rows: 2, cols: 2, label: '2×2' },
  { rows: 2, cols: 3, label: '2×3' },
  { rows: 3, cols: 3, label: '3×3' },
] as const;

/** Labels and descriptions live under `videoWalls:modes.<value>`. */
export const WALL_MODE_OPTIONS = [
  { value: 'extend' as const },
  { value: 'mirror' as const },
] as const;

export function tileIndex(row: number, col: number, cols: number): number {
  return row * cols + col;
}

/** Physical position on the wall: "Left" is the viewer's left in every language. */
export function tileLabel(row: number, col: number, rows: number, cols: number): string {
  const vertical =
    rows === 1
      ? ''
      : row === 0
        ? i18n.t('videoWalls:tile.top')
        : row === rows - 1
          ? i18n.t('videoWalls:tile.bottom')
          : i18n.t('videoWalls:tile.row', { number: row + 1 });
  const horizontal =
    cols === 1
      ? ''
      : col === 0
        ? i18n.t('videoWalls:tile.left')
        : col === cols - 1
          ? i18n.t('videoWalls:tile.right')
          : i18n.t('videoWalls:tile.col', { number: col + 1 });
  if (vertical && horizontal) {
    return i18n.t('videoWalls:tile.position', { vertical, horizontal });
  }
  return vertical || horizontal || i18n.t('videoWalls:tile.centre');
}

/** The wall's operational state. Computed by the server; the dashboard only displays it. */
export function wallStatus(wall: VideoWall): VideoWallOperationalState {
  return wall.operations.state;
}

export function wallStatusLabel(
  state: VideoWallOperationalState,
  mode?: VideoWall['mode'] | null,
): string {
  switch (state) {
    case 'no_content':
      return i18n.t('videoWalls:status.noContent');
    case 'no_screens':
      return i18n.t('videoWalls:status.noScreens');
    case 'slicing':
      // Mirror doesn't slice: its job validates the playback file.
      return mode === 'mirror'
        ? i18n.t('videoWalls:status.slicingMirror')
        : i18n.t('videoWalls:status.slicing');
    case 'failed':
      return i18n.t('videoWalls:status.failed');
    case 'waiting_for_tiles':
      return i18n.t('videoWalls:status.waitingForTiles');
    case 'starting':
      return i18n.t('videoWalls:status.starting');
    case 'on_air':
      return i18n.t('videoWalls:status.onAir');
    case 'interrupted':
      return i18n.t('videoWalls:status.interrupted');
    case 'stopped':
      return i18n.t('videoWalls:status.stopped');
  }
}

/** Badge colour for a wall state. */
export function wallStatusColor(state: VideoWallOperationalState): string {
  switch (state) {
    case 'on_air':
      return 'var(--gs-ok)';
    case 'failed':
    case 'interrupted':
      return 'var(--gs-bad)';
    case 'slicing':
    case 'waiting_for_tiles':
    case 'starting':
    case 'no_screens':
      return 'var(--gs-warn)';
    case 'no_content':
    case 'stopped':
      return 'var(--gs-muted)';
  }
}

export type PublishStep = 'prepare' | 'ready' | 'commit' | 'failed';

/** Publish stepper position for the server-computed wall state. A failed publish is its own step, never Prepare. */
export function publishStep(
  state: VideoWallOperationalState | null | undefined,
): PublishStep | null {
  switch (state) {
    case 'slicing':
      return 'prepare';
    case 'failed':
      return 'failed';
    case 'waiting_for_tiles':
      return 'ready';
    case 'starting':
    case 'on_air':
    case 'interrupted':
      return 'commit';
    default:
      return null;
  }
}

export function manifestLabel(publication: VideoWallPublication | null | undefined): string {
  if (!publication) return '—';
  return `v${publication.id.slice(0, 4)}`;
}

export function formatBezelMm(value: number): string {
  return i18n.t('videoWalls:bezelMm', { value: Math.round(value) });
}

/** Tile connection colour: unknown is amber, never shown as offline. */
export function tileConnectionColor(connection: VideoWallTileConnection): string {
  if (connection === 'online') return 'var(--gs-ok)';
  if (connection === 'offline') return 'var(--gs-bad)';
  return 'var(--gs-warn)';
}

export function tileConnectionLabel(connection: VideoWallTileConnection): string {
  if (connection === 'online') return i18n.t('videoWalls:connection.online');
  if (connection === 'offline') return i18n.t('videoWalls:connection.offline');
  return i18n.t('videoWalls:connection.unknown');
}

/** Refetch interval for a wall: fast while it gets ready, slower while it plays so an interruption shows up. */
export function wallPollMs(state: VideoWallOperationalState | null | undefined): number | false {
  if (state === 'slicing' || state === 'waiting_for_tiles' || state === 'starting') return 3_000;
  if (state === 'on_air' || state === 'interrupted') return 15_000;
  return false;
}

/** Age of a timestamp as short visible text, e.g. "12 s ago". */
export function formatAge(iso: string, now: number): string {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return i18n.t('videoWalls:age.unknown');
  if (ms < 0) {
    return ms > -5_000 ? i18n.t('videoWalls:age.justNow') : i18n.t('videoWalls:age.ahead');
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return i18n.t('videoWalls:age.seconds', { value: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return i18n.t('videoWalls:age.minutes', { value: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return i18n.t('videoWalls:age.hours', { value: hours });
  return i18n.t('videoWalls:age.days', { value: Math.floor(hours / 24) });
}

export type TileReadyLabel = 'ready' | 'readyLastReported' | 'waiting';

/**
 * READY is only shown as live when the tile is also online. An offline or
 * unknown tile keeps its last reported READY, labelled as such, so the row
 * matches the live READY count in the header and the Interrupted message.
 */
export function tileReadyLabel(
  ready: boolean,
  connection: VideoWallTileConnection,
): TileReadyLabel {
  if (!ready) return 'waiting';
  return connection === 'online' ? 'ready' : 'readyLastReported';
}
